import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { nanoid } from 'nanoid'
import { getIsValidAuthNumber } from 'shared/helpers/getIsValidAuthNumber'
import type { IDropdownItem } from 'shared/ui'
import { CountryEnum } from '../types'

type IValidateNumberFn = (number: string, country: IDropdownItem) => boolean

export class PhoneInputStore {
  private _disposeNumberValidation: IReactionDisposer | null = null
  private _numberError: string | null = null
  private _numberRequestError: string | null = null
  private _numberValidationTrigger: string | null = null
  private _number = ''
  private _country: IDropdownItem
  private _excludeCountries: CountryEnum[] = []
  private _customCountries: IDropdownItem[] | null = null
  private _validateFn: IValidateNumberFn | null = null
  private _invalidNumberMessage = 'Please enter a valid phone number'

  constructor({
    excludeCountries,
    number,
    validate,
    invalidNumberMessage,
  }: {
    excludeCountries?: CountryEnum[]
    number?: string
    validate?: IValidateNumberFn
    invalidNumberMessage?: string
  } = {}) {
    if (excludeCountries) this._excludeCountries = excludeCountries
    if (number) this._number = number
    if (validate) this._validateFn = validate
    if (invalidNumberMessage) this._invalidNumberMessage = invalidNumberMessage
    this._country = this.defaultCountry
    makeAutoObservable(this)

    this.reactionNumberValidation()
  }

  isValidCountryEnum = (id: string | number): id is CountryEnum => {
    if (typeof id !== 'string') return false

    const validCountries: string[] = Object.values(CountryEnum)

    return validCountries.includes(id)
  }

  get number() {
    return this._number
  }

  get fullNumber() {
    return `${this.country.labelRight}${this._number}`
  }

  get fullUnformattedNumber() {
    return `${this.country.labelRight}${this._number.replace(/[^0-9]/g, '')}`
  }

  get country() {
    return this._country
  }

  get isValidNumber() {
    if (this._validateFn) return this._validateFn(this.number, this.country)

    let countryCode

    if (this.isValidCountryEnum(this.country.id)) countryCode = this.country.id

    return getIsValidAuthNumber(this.number, countryCode)
  }

  get numberError() {
    return this._numberError
  }

  get numberRequestError() {
    return this._numberRequestError
  }

  get USCountry(): IDropdownItem {
    return {
      label: 'United States',
      id: CountryEnum.US,
      iconL: 'flagUSA',
      labelRight: '+1',
      labelRightVariant: 'body-md-regular',
    }
  }

  get defaultCountry(): IDropdownItem {
    return this.countries?.[0] ?? this.USCountry
  }

  get countries(): IDropdownItem[] {
    if (this._customCountries) return this._customCountries

    const _countries: IDropdownItem[] = [
      this.USCountry,
      {
        label: 'Canada',
        id: CountryEnum.CA,
        iconL: 'flagCanada',
        labelRight: '+1',
        labelRightVariant: 'body-md-regular',
      },
      {
        label: 'United Kingdom',
        id: CountryEnum.GB,
        iconL: 'flagUK',
        labelRight: '+44',
        labelRightVariant: 'body-md-regular',
      },
      {
        label: 'Australia',
        id: CountryEnum.AU,
        iconL: 'flagAU',
        labelRight: '+61',
        labelRightVariant: 'body-md-regular',
      },
    ]

    return this._excludeCountries && this._excludeCountries.length > 0
      ? _countries.filter((country) => !this._excludeCountries.includes(country.id as CountryEnum))
      : _countries
  }

  setCustomCountries = (countries: IDropdownItem[] | null) => {
    this._customCountries = countries
  }

  triggerNumberValidation = () => {
    this._numberValidationTrigger = nanoid()
  }

  setNumber = (value: string) => {
    if (this._numberRequestError) this.setNumberRequestError('')
    this._number = value
  }

  setNumberError = (value: string) => {
    this._numberError = value
  }

  setNumberRequestError = (value: string) => {
    this._numberRequestError = value
  }

  setCountry = (value: IDropdownItem) => {
    this._country = value

    this.checkValidity(value)
  }

  checkValidity(value: IDropdownItem) {
    if (this.isValidCountryEnum(value.id)) {
      const cleanedNumber = this._number.replace(/\D/g, '')
      const newMaxLength = value.id === CountryEnum.AU ? 9 : 10

      if (cleanedNumber.length > newMaxLength) {
        this._number = cleanedNumber.slice(0, newMaxLength)
      }
    }

    if (this._number) {
      this.triggerNumberValidation()
    }
  }

  setCountryById = (value: string) => {
    const filteredCountry = this.countries.filter((el) => el.id === value)

    if (!filteredCountry.length) return

    this._country = filteredCountry[0]
  }

  setExcludeCountries = (countries: CountryEnum[]) => {
    this._excludeCountries = countries
  }

  reset = () => {
    this._number = ''
    this._numberError = null
    this._numberRequestError = null
    this._numberValidationTrigger = null
    this._country = this.defaultCountry
  }

  reactionNumberValidation = () => {
    this._disposeNumberValidation?.()
    this._disposeNumberValidation = reaction(
      () => [this._numberValidationTrigger, this.isValidNumber],
      ([trigger, isValid]) => {
        this._numberError = trigger && !isValid ? this._invalidNumberMessage : null
        if (isValid) this._numberValidationTrigger = null
      },
      {
        fireImmediately: true,
      }
    )
  }
}
